import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiDialog, TuiIcon } from '@taiga-ui/core'
import { TuiSelectModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { OrderTermSelectComponent } from '@shared/components/trading/orders/common/order-term-select/order-term-select.component'
import { OrderTypeSelectComponent } from '@shared/components/trading/orders/common/order-type-select/order-type-select.component'
import { TuiCheckbox } from '@taiga-ui/kit'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { OrderTerms } from '@modules/orders-module/constants/order-terms'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { Subject, takeUntil } from 'rxjs'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { HttpErrorResponse } from '@angular/common/http'
import { ClosePositionInterface } from '@modules/positions-module/interfaces/close-position.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { QuantitySelectorComponent } from '@shared/components/trading/positions/stocks/common/quantity-selector/quantity-selector.component'
import { ConfirmClosePositionComponent } from '@shared/components/trading/positions/stocks/common/close-position/common/confirm-close-position/confirm-close-position.component'
import { AlertService } from '@shared/services/alert.service'
import { PositionsStocksService } from '@modules/stocks-module/positions/services/positions-stocks.service'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-close-position',
	templateUrl: './close-position.component.html',
	styleUrl: './close-position.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TuiDialog,
		TuiCheckbox,
		TranslatePipe,
		TuiTextfieldControllerModule,
		TuiSelectModule,
		ReactiveFormsModule,
		OrderTermSelectComponent,
		OrderTypeSelectComponent,
		QuantitySelectorComponent,
		TuiTextfieldControllerModule,
		ConfirmClosePositionComponent,
		ButtonComponent,
		OrderPriceInputComponent,
	],
})
export class ClosePositionComponent implements OnInit, OnDestroy {
	protected readonly orderTerms: OrderTermEnum[] = OrderTerms
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum

	public position: InputSignal<PositionsInterface | null> = input<PositionsInterface | null>(null)

	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _positionsStocksService: PositionsStocksService =
		inject<PositionsStocksService>(PositionsStocksService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)

	protected form: FormGroup | null = null
	protected isCanceledOpenedOrders: boolean = false

	protected isLoadingOrders: WritableSignal<boolean> = signal<boolean>(false)
	protected isOpenConfirmDialog: WritableSignal<boolean> = signal<boolean>(false)
	protected isClosePositionLoading: WritableSignal<boolean> = signal<boolean>(false)

	get isVisibleLimitPrice(): boolean {
		return this._ordersStocksHelperService.checkIsLimitPriceVisible(this.form?.get('type')?.value)
	}

	get orderTermsList(): OrderTermEnum[] {
		return this.form?.get('type')?.value === this.orderTypeEnum.MARKET
			? [this.orderTermEnum.DAY, this.orderTermEnum.GTC]
			: this.orderTerms
	}

	get orderTypesList(): OrderTypeEnum[] {
		return [this.orderTypeEnum.MARKET, this.orderTypeEnum.LIMIT]
	}

	get isShowConfirmDialog(): boolean {
		const formValue: ClosePositionInterface = this.form?.value

		return (
			formValue.type !== this.orderTypeEnum.LIMIT &&
			formValue.time_in_force !== this.orderTermEnum.DAY_PLUS &&
			this._marketStatusHelperService.marketStatus !== this.marketStatusEnum.marketOpen
		)
	}
	private _initForm(): void {
		this.form = this._formBuilder.group({
			qty: [0, Validators.required],
			symbol: ['', Validators.required],
			type: [this.orderTypeEnum.MARKET, Validators.required],
			limit_price: [null],
			time_in_force: [this.orderTermEnum.GTC, Validators.required],
			extended_hours: [false],
			cancel_open_orders: [false],
		})
	}

	private _fillForm(): void {
		if (!this.form || !this.position()) return

		this.form.patchValue(
			{
				symbol: this.position()?.symbol,
				limit_price: +(this.position()?.current_price ?? 0),
				cancel_open_orders: this.isCanceledOpenedOrders,
			},
			{ emitEvent: false },
		)
	}

	private _subscribeToFormTypeChanges(): void {
		this.form
			?.get('type')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((): void => {
				if (!this.orderTermsList.includes(this.form?.get('time_in_force')?.value)) {
					this.form?.patchValue({ time_in_force: this.orderTermEnum.DAY }, { emitEvent: false })
				}
			})
	}

	private _listenLoadingOpenedOrders(): void {
		this._ordersStocksHelperService.ordersLoadingMap
			.get(this.orderStateEnum.OPEN)
			?.pipe(takeUntil(this._destroy$))
			.subscribe((loading: boolean): void => {
				this.isLoadingOrders.set(loading)
			})
	}

	protected onChangeQty(qty: number): void {
		this.form?.patchValue({ qty })
	}

	protected closePosition(): void {
		if (!this.form?.value) return
		const order: ClosePositionInterface = this.form.value

		this.isClosePositionLoading.set(true)

		this._positionsStocksService
			.closePosition(this._positionsStocksHelperService.transformOrderForClosePosition(order))
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('positions.positionSuccessClose'))
					this.isClosePositionLoading.set(false)
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ||
							this._translateService.instant("positions.couldn'tClosePosition"),
					)
					this.isClosePositionLoading.set(false)
				},
			)
	}

	protected onSubmit(): void {
		if (this.form?.invalid) return

		if (this._marketStatusHelperService.marketStatus === this.marketStatusEnum.marketOpen) {
			this.closePosition()
		}
	}

	protected cancelOpenedOrders(checked: boolean): void {
		if (!this.form || !this.position()) return

		this.isCanceledOpenedOrders = checked
		const formQty: number = this.form.get('qty')?.value ?? 0

		this.form.patchValue({
			qty: checked ? this.position()?.qty : formQty,
			cancel_open_orders: checked,
		})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	protected onToggleConfirmDialog(value: boolean): void {
		this.isOpenConfirmDialog.set(value)
	}

	ngOnInit(): void {
		this._initForm()
		this._fillForm()
		this._listenLoadingOpenedOrders()
		this._subscribeToFormTypeChanges()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
